import { TablePageChangeEvent } from '@servicetitan/design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAreaCode, normilizePhone } from '../../phone-umbers-utils';
import { TenantPhoneNumberItem } from '../../types';
import {
  NAMED_FILTER_OPTION,
  useOutboundCallerIdFilters,
} from './useOutboundCallerIdFilters';

type UseTablePaginationProps = {
  data: TenantPhoneNumberItem[];
  initSelectedItem?: TenantPhoneNumberItem | null;
  loading?: boolean;
};

export const defaultPageSize = 10;
export const pageSizes = [...new Set([10, 25, 50, defaultPageSize])].sort(
  (a, b) => a - b,
);
const defaultPage = () => ({ skip: 0, take: defaultPageSize });

const getSkeletonItem = () => ({
  name: 'Loading...',
  number: '(000)-000-0000',
  campaignName: 'Loading',
  loading: true,
});

const skeletonData = new Array(15).fill(getSkeletonItem());

export const useOutboundCallerIdTable = ({
  data = [],
  initSelectedItem,
  loading,
}: UseTablePaginationProps) => {
  const filters = useOutboundCallerIdFilters();
  const { value, areaCodes, namedFilter } = filters;
  const [page, setPage] = useState(defaultPage());
  const [selectedItem, setSelectedItem] =
    useState<TenantPhoneNumberItem | null>(() => initSelectedItem ?? null);
  const [items, setItems] = useState(data);

  useEffect(() => {
    setItems(data);
  }, [data]);

  useEffect(() => {
    setSelectedItem(initSelectedItem ?? null);
  }, [initSelectedItem]);

  const onSelectionChange = useCallback((event: any) => {
    const newItem = event.dataItem;
    if (!newItem?.id) return;
    setSelectedItem((prev: TenantPhoneNumberItem | null) => {
      if (!newItem || prev?.id === newItem?.id) {
        return prev;
      }

      setItems((prevItems) => {
        const found = prevItems.find((item) => item.id === newItem.id);
        if (found?.selected) {
          return prevItems;
        }

        return prevItems.map((item) => ({
          ...item,
          selected: item.id === newItem.id,
        }));
      });
      return newItem;
    });
  }, []);

  const onPageChange = useCallback(
    (e: TablePageChangeEvent) => {
      if (page.take !== e.page.take) {
        setPage({ skip: 0, take: e.page.take });
      } else {
        setPage({ skip: e.page.skip, take: e.page.take });
      }
    },
    [page.take],
  );

  const filteredData = useMemo(() => {
    const val = value?.trim().toLowerCase();
    const valPhone = normilizePhone(val);

    const res = items.filter((item) => {
      const matchesSearch = val
        ? item.name?.toLowerCase().includes(val) ||
          item.campaignName?.toLowerCase().includes(val) ||
          (valPhone && item.number.includes(valPhone))
        : true;

      const areaCode = getAreaCode(item.number);
      const matchesAreaCode =
        areaCodes.length === 0 || (areaCode && areaCodes.includes(areaCode));

      const matchesNamedFilter =
        namedFilter !== NAMED_FILTER_OPTION || !!item.name;

      return matchesSearch && matchesAreaCode && matchesNamedFilter;
    });
    return res;
  }, [items, value, areaCodes, namedFilter]);

  useEffect(() => {
    if (page.skip >= filteredData.length) {
      setPage({
        skip: Math.max(0, filteredData.length - page.take),
        take: page.take,
      });
    }
  }, [filteredData.length, page.skip, page.take, onPageChange]);

  const paginatedData = useMemo(() => {
    const startIndex = page.skip;
    const endIndex = startIndex + page.take;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page.skip, page.take]);

  return {
    table: {
      data: loading ? skeletonData : paginatedData,
      totalCount: filteredData.length,
      page,
      onPageChange,
      pageSizes,
      selectedItem,
      onSelectionChange,
    },
    filters,
    onClear: () => {
      filters.onClear();
      setPage(defaultPage());
    },
  };
};
